import { useEffect, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { Download } from "lucide-react";
import { api, errMsg, TOKEN_KEY } from "@/lib/api";
import { ExportProgress, fmtBytes } from "@/components/account/YourData";

const BASE = process.env.REACT_APP_BACKEND_URL;
const MSG = {
  expired: "This download has expired. The file was deleted after 7 days. Make a new copy in Settings → Your data.",
  used: "This link has already been used. Sign in and open Settings → Your data to download it again.",
  invalid: "This link isn't valid. Sign in and open Settings → Your data.",
  preparing: "Your export is still being prepared.",
  queued: "Your export is still being prepared.",
  failed: "We couldn't finish your export. Try again.",
};

export default function ExportDownload() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const t = sp.get("t");
  const signedIn = !!localStorage.getItem(TOKEN_KEY);
  const [st, setSt] = useState(null);
  const [job, setJob] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (t) api.get(`/exports/${id}/link-status`, { params: { t } }).then(({ data }) => setSt(data)).catch((x) => setErr(errMsg(x)));
    else if (signedIn) api.get(`/exports/${id}`).then(({ data }) => setJob(data)).catch((x) => setErr(errMsg(x)));
    else setErr("Sign in as the person who asked for this export to download it.");
  }, [id, t, signedIn]);
  if (!t && !signedIn) {
    sessionStorage.setItem("qhh.returnTo", `/download/${id}${sp.get("auto") ? "?auto=1" : ""}`);
    return <Navigate to="/signin" replace />;
  }
  return (
    <main className="flex min-h-screen items-center justify-center bg-q-bg p-6" data-testid="export-download-page">
      <div className="q-card w-full max-w-md p-8">
        <h1 className="font-display text-3xl font-extrabold text-q-text">{sp.get("auto") ? "Your family backup" : "Your data export"}</h1>
        <div className="mt-4">
          {err && <p className="font-semibold text-red-300" role="alert" data-testid="export-link-error">{err}</p>}
          {job && <ExportProgress job={job} />}
          {st && (st.status === "ready" ? (
            <div className="space-y-3" data-testid="export-link-ready">
              <p className="text-q-muted">{fmtBytes(st.size)}{st.encrypted ? " · protected with the ZIP password you chose" : ""}. This link works once.</p>
              <a className="q-btn q-btn-primary min-h-[44px] w-full" href={`${BASE}/api/exports/${id}/download?t=${encodeURIComponent(t)}`} data-testid="export-link-download"><Download className="h-4 w-4" /> Download ZIP</a>
            </div>
          ) : <p className="font-semibold text-q-text" data-testid="export-link-status">{MSG[st.status] || MSG.invalid}</p>)}
        </div>
        <Link to="/signin" className="mt-6 inline-block text-sm text-q-muted underline" data-testid="export-link-signin">Go to The Queue</Link>
      </div>
    </main>
  );
}
